import clsx from 'clsx';
import {useEffect} from 'react';
import useHorizontalScroll from '@di-shared/utils/useHorizontalScroll';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import {emitEventLog} from '@di-shared/features/analytics';

interface SafeDrivingMonthlyGraphKrProps {
  dashedLineScore: number;
  coloredItemScoreThreshold?: number;
  items: Omit<ItemProps, 'isActive' | 'onClick'>[];
}

const SCALE_FACTOR = 1.2;

function SafeDrivingMonthlyGraph({dashedLineScore, items, coloredItemScoreThreshold}: SafeDrivingMonthlyGraphKrProps) {
  const {t} = useInferableTranslation();
  const {scrollRef, scrollToStart, scrollToEnd, isAtLeft, isScrollable, isAtRight} = useHorizontalScroll<HTMLDivElement>();
  const isDataLessThanSix = items.length < 6;

  useEffect(() => {
    scrollToEnd();
  }, []);

  return (
    <div className={clsx('monthly_graph_scroll_wrap', {type_center: isDataLessThanSix})}>
      <div
        className="bar_graph_average"
        style={{bottom: 26 + dashedLineScore * SCALE_FACTOR}}
      >
        <span className="average_line"></span>
        <span className="average_text_box">
          <span className="average_text">{t('T20006_$score', {score: dashedLineScore})}</span>
        </span>
      </div>

      <div
        className="statistics_graph_area"
        ref={scrollRef}
      >
        <div
          className={clsx('bar_graph', {is_center: isDataLessThanSix})}
          style={{}}
        >
          {items.map(({score, label}) => (
            <Item
              key={label}
              score={score}
              label={label}
              isActive={(!!score || score === 0) && score >= (coloredItemScoreThreshold ?? Infinity)}
            />
          ))}
        </div>
      </div>
      {isScrollable && !isAtLeft && (
        <button
          type="button"
          className="btn_prev"
          onClick={() => {
            emitEventLog({
              event: 'di_driveinfo_interaction',
              data_action: 'slide banner',
              data_name: t('T30472'),
            });
            scrollToStart();
          }}
        >
          <span className="ic ic_arrow_back_16px">
            <span className="blind">{t('T200006')}</span>
          </span>
        </button>
      )}
      {isScrollable && !isAtRight && (
        <button
          type="button"
          className="btn_next"
          onClick={() => {
            emitEventLog({
              event: 'di_driveinfo_interaction',
              data_action: 'slide banner',
              data_name: t('T30472'),
            });
            scrollToEnd();
          }}
        >
          <span
            className="ic ic_arrow_forward_16px"
            aria-hidden="true"
          >
            <span className="blind">{t('T200007')}</span>
          </span>
        </button>
      )}
    </div>
  );
}

interface ItemProps {
  score: number | null;
  label: string;
  isActive?: boolean;
  onClick?: () => void;
}

function Item({score = 0, label = '', isActive = false, onClick = () => {}}: ItemProps): JSX.Element {
  return (
    <div
      className={clsx('bar_graph_item', {'is-active': isActive})}
      onClick={onClick}
    >
      <span className="graph_score">{score}</span>
      <span
        className="graph_bar"
        style={{height: (score ?? 0) * SCALE_FACTOR}}
      ></span>
      <span
        className="graph_title"
        style={{whiteSpace: 'pre-wrap'}}
      >
        {label}
      </span>
    </div>
  );
}

export default SafeDrivingMonthlyGraph;
